import React from 'react';

const shimmer: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
  backgroundSize: '200% 100%',
  animation: 'lhShimmer 2s infinite',
};

interface LetterheadsSkeletonLoaderProps {
  /** 'letterheads': # · Title · Created By · Created At · Actions
   *  'templates'  : # · Title · Type · Status · Created By · Created At · Actions */
  variant?: 'letterheads' | 'templates';
  rows?: number;
}

const LetterheadsSkeletonLoader: React.FC<LetterheadsSkeletonLoaderProps> = ({
  variant = 'letterheads',
  rows = 10,
}) => {
  const isTemplates = variant === 'templates';
  const actionCount = isTemplates ? 2 : 3; // templates: edit, delete · letterheads: edit, view, delete

  return (
    <>
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* Info bar */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f3f4f6',
            background: '#fafbfc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, ...shimmer }} />
            <div style={{ width: 220, height: 16, borderRadius: 8, ...shimmer }} />
          </div>
          <div style={{ width: 90, height: 32, borderRadius: 8, ...shimmer }} />
        </div>

        {/* Rows */}
        <div style={{ padding: 20 }}>
          {[...Array(rows)].map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                borderBottom: index < rows - 1 ? '1px solid #f3f4f6' : 'none',
                animation: 'lhPulse 1.5s ease-in-out infinite',
                animationDelay: `${index * 0.1}s`,
              }}
            >
              {/* # */}
              <div style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, ...shimmer }} />

              {/* Title (avatar + title + serial no.) */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, ...shimmer }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ width: '65%', height: 14, borderRadius: 8, marginBottom: 8, ...shimmer }} />
                  <div style={{ width: '40%', height: 11, borderRadius: 8, ...shimmer }} />
                </div>
              </div>

              {/* Type pill (templates only) */}
              {isTemplates && (
                <div style={{ width: 140, flexShrink: 0 }}>
                  <div style={{ width: 100, height: 26, borderRadius: 8, ...shimmer }} />
                </div>
              )}

              {/* Status pill (templates only) */}
              {isTemplates && (
                <div style={{ width: 110, flexShrink: 0 }}>
                  <div style={{ width: 80, height: 26, borderRadius: 20, ...shimmer }} />
                </div>
              )}

              {/* Created by */}
              <div style={{ width: isTemplates ? 200 : 220, flexShrink: 0 }}>
                <div style={{ width: '70%', height: 14, borderRadius: 8, ...shimmer }} />
              </div>

              {/* Created at */}
              <div style={{ width: 160, flexShrink: 0 }}>
                <div style={{ width: 100, height: 14, borderRadius: 8, ...shimmer }} />
              </div>

              {/* Actions */}
              <div style={{ width: 120, display: 'flex', gap: 4, justifyContent: 'center', flexShrink: 0 }}>
                {[...Array(actionCount)].map((_, i) => (
                  <div key={i} style={{ width: 32, height: 32, borderRadius: '50%', ...shimmer }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div
          style={{
            background: '#fafbfc',
            borderTop: '1px solid #e5e7eb',
            padding: '20px 16px',
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {[...Array(7)].map((_, i) => (
            <div key={i} style={{ width: 36, height: 36, borderRadius: 8, ...shimmer }} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes lhShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes lhPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default LetterheadsSkeletonLoader;